// Phase SIKA-5 Layout — Panel 3: Plan Timeline (LINE CHART, full-width).
//
// Source: video frames 4/9 — line chart of cumulative current_amount per
// week, with a dashed goal line and a dashed projection from now to
// week 13. Replaces the SIKA-2 per-week HairlineRow list.
//
// Doctrine:
//   N06 — Recalibrated cycles render the RECALIBRATED indicator (here a
//         small chip below the chart, since the chart itself does not
//         carry per-cycle badges). The full-history Portfolio surface
//         keeps the per-cycle badge — this panel summarises trajectory.
//   N09 — When the last 3 settled cycles are LOST, render the verbatim
//         Table 19 warning panel ABOVE the chart.
//
// Composes Eyebrow + Pill + Badge. The chart geometry is built by the
// pure helper src/sika/planTimelineSvg.ts (Vitest-pinned).

import { useMemo } from "react";
import { Badge, Eyebrow, Pill } from "../super-acca";
import {
  buildPlanTimelineSvg,
  derivePaceState,
  formatCompactAmount,
  type PlanTimelineCycleInput,
  type PlanTimelineWeekPoint,
} from "../../sika/planTimelineSvg.ts";
import type { SikaCycleRow, SikaGoalRow } from "../../sika/useSikaGoal.ts";

export interface PlanTimelinePanelProps {
  goal: SikaGoalRow | null;
  cycles: SikaCycleRow[];
  pro?: boolean;
}

function ghc(amount: number): string {
  return `GH₵${amount.toLocaleString("en-GH", { maximumFractionDigits: 0 })}`;
}

function weeksRemaining(deadline: string | null): number {
  if (!deadline) return 0;
  const ms = new Date(deadline).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return Math.ceil(ms / (7 * 24 * 60 * 60 * 1000));
}

function totalWeeks(goal: SikaGoalRow): number {
  if (!goal.deadline) return 13;
  const created = new Date(goal.created_at).getTime();
  const end = new Date(goal.deadline).getTime();
  if (!Number.isFinite(end) || end <= created) return 13;
  return Math.max(1, Math.ceil((end - created) / (7 * 24 * 60 * 60 * 1000)));
}

function pctFunded(current: number, target: number): number {
  if (target <= 0) return 0;
  return Math.round((current / target) * 1000) / 10;
}

function lastThreeSettled(cycles: SikaCycleRow[]): SikaCycleRow[] {
  const settled = cycles.filter((c) => c.outcome !== null);
  const sorted = [...settled].sort((a, b) => b.cycle_number - a.cycle_number);
  return sorted.slice(0, 3);
}

function hasThreeConsecutiveLosses(cycles: SikaCycleRow[]): boolean {
  const last3 = lastThreeSettled(cycles);
  if (last3.length < 3) return false;
  return last3.every((c) => c.outcome === "lost");
}

export function PlanTimelinePanel({ goal, cycles, pro = false }: PlanTimelinePanelProps) {
  const chartInput: PlanTimelineCycleInput[] = useMemo(() => {
    // Build cumulative per-cycle from stake/return where available. Falls
    // back to incrementing per settled cycle. Honest: we only plot what
    // we know — settled cycles produce real points.
    const sorted = [...cycles].sort((a, b) => a.cycle_number - b.cycle_number);
    let running = 0;
    return sorted.map((c) => {
      const settled = c.outcome === "won" || c.outcome === "lost";
      if (c.outcome === "won" && c.return_amount !== null) {
        running += Number(c.return_amount) - Number(c.stake_amount ?? 0);
      }
      return {
        cycle_number: c.cycle_number,
        current_amount: Math.max(0, running),
        settled,
      };
    });
  }, [cycles]);

  if (!goal) {
    return (
      <div style={{ padding: 18 }} data-panel="plan-timeline">
        <Eyebrow>Panel 3 · Plan Timeline</Eyebrow>
        <div
          style={{
            marginTop: 12,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 13,
            color: "var(--sa-fg-muted)",
          }}
        >
          Set a goal to see the weekly plan.
        </div>
      </div>
    );
  }

  const threeLosses = hasThreeConsecutiveLosses(cycles);
  const pct = pctFunded(goal.current_amount, goal.target_amount);
  const wks = weeksRemaining(goal.deadline);
  const total = totalWeeks(goal);
  const currentWeek = Math.max(1, total - wks);
  const latest = cycles.length > 0 ? cycles[cycles.length - 1] : null;
  const nextTarget = latest?.dynamic_target ?? 0;
  // Use real current_amount as the "now" anchor so the chart matches Panel 1.
  const anchorPoint: PlanTimelineCycleInput = {
    cycle_number: currentWeek,
    current_amount: goal.current_amount,
    settled: true,
  };
  const merged = chartInput.some((c) => c.cycle_number === currentWeek)
    ? chartInput
    : [...chartInput, anchorPoint];

  const svg = buildPlanTimelineSvg({
    goal: goal.target_amount,
    totalWeeks: total,
    currentWeek,
    cycles: merged,
    width: 720,
    height: 220,
  });

  const recalibratedCount = cycles.filter((c) => c.recalibrated).length;

  return (
    <div style={{ padding: 22 }} data-panel="plan-timeline">
      {threeLosses ? (
        <div
          data-warning="three-consecutive-losses"
          style={{
            marginBottom: 16,
            padding: 14,
            border: "1px solid var(--sa-signal-red)",
            borderRadius: 3,
            background: "var(--sa-signal-red-dim)",
          }}
        >
          <div
            style={{
              fontFamily: "var(--sa-font-stat)",
              fontSize: 13,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "var(--sa-signal-red)",
            }}
          >
            3 consecutive losses
          </div>
          <div
            style={{
              marginTop: 8,
              fontFamily: "var(--sa-font-mono)",
              fontSize: 12,
              color: "var(--sa-fg-secondary)",
              lineHeight: 1.5,
            }}
          >
            Goal progress: {String(pct)}%. With {String(wks)} weeks remaining,
            the plan remains achievable if the next cycles win at{" "}
            {nextTarget.toFixed(2)}×.
          </div>
        </div>
      ) : null}

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <Eyebrow>Panel 3 · Plan Timeline</Eyebrow>
          <div
            style={{
              marginTop: 6,
              fontFamily: "var(--sa-font-stat)",
              fontSize: 20,
              letterSpacing: "0.02em",
              color: "var(--sa-fg-primary)",
            }}
          >
            Plan Timeline
          </div>
          <div
            style={{
              marginTop: 4,
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11,
              color: "var(--sa-fg-muted)",
              letterSpacing: "0.06em",
            }}
          >
            Week {String(currentWeek)} of {String(total)} · Trajectory{" "}
            {svg.onPace ? "meets goal" : "needs lift"} by week {String(total)}
          </div>
        </div>
        <Badge variant={svg.onPace ? "success" : "amber"}>{svg.onPace ? "ON PACE" : "OFF PACE"}</Badge>
      </div>

      {/* LINE CHART */}
      <div style={{ marginTop: 18, overflowX: "auto" }} data-testid="sika-plan-timeline-chart">
        <svg
          viewBox={`0 0 ${String(svg.width)} ${String(svg.height)}`}
          width="100%"
          style={{ display: "block", maxWidth: "100%", height: "auto" }}
          aria-label="Plan Timeline trajectory"
        >
          {/* X-axis baseline */}
          <line
            x1={svg.paddingX}
            y1={svg.height - svg.paddingY}
            x2={svg.width - svg.paddingX}
            y2={svg.height - svg.paddingY}
            stroke="var(--sa-hairline)"
            strokeWidth={1}
          />
          {/* Goal line (dashed) */}
          <line
            x1={svg.paddingX}
            y1={svg.goalLineY}
            x2={svg.width - svg.paddingX}
            y2={svg.goalLineY}
            stroke="var(--sa-accent-saffron)"
            strokeWidth={1}
            strokeDasharray="4 4"
            opacity={0.6}
          />
          <text
            x={svg.width - svg.paddingX}
            y={svg.goalLineY - 6}
            textAnchor="end"
            style={{ fontFamily: "var(--sa-font-mono)", fontSize: 10, fill: "var(--sa-accent-saffron)" }}
          >
            {ghc(goal.target_amount)}
          </text>
          {/* X-axis week ticks (every 1 if total<=13, else every 2) */}
          {Array.from({ length: total + 1 }).map((_, i) => {
            if (total > 13 && i % 2 !== 0) return null;
            const x = svg.xScale(i);
            return (
              <g key={i}>
                <line
                  x1={x}
                  y1={svg.height - svg.paddingY}
                  x2={x}
                  y2={svg.height - svg.paddingY + 4}
                  stroke="var(--sa-hairline)"
                />
                {i > 0 ? (
                  <text
                    x={x}
                    y={svg.height - svg.paddingY + 16}
                    textAnchor="middle"
                    style={{ fontFamily: "var(--sa-font-mono)", fontSize: 9, fill: "var(--sa-fg-muted)" }}
                  >
                    W{String(i).padStart(2, "0")}
                  </text>
                ) : null}
              </g>
            );
          })}
          {/* Projected (dashed) — render BEHIND actuals */}
          {svg.projectedPath ? (
            <path
              d={svg.projectedPath}
              fill="none"
              stroke="var(--sa-accent-saffron)"
              strokeWidth={1.5}
              strokeDasharray="6 4"
              opacity={0.55}
            />
          ) : null}
          {/* Actual (solid gold) */}
          {svg.actualPath ? (
            <path
              d={svg.actualPath}
              fill="none"
              stroke="var(--sa-accent-saffron)"
              strokeWidth={2}
            />
          ) : null}
          {/* Data points */}
          {svg.points.map((p) => (
            <circle
              key={p.week}
              cx={p.x}
              cy={p.y}
              r={3.5}
              fill="var(--sa-accent-saffron)"
              stroke="var(--sa-bg-primary)"
              strokeWidth={1}
            />
          ))}
        </svg>
      </div>

      {/* W01..W13 stat cards row (frame_018) */}
      <WeekCardsRow weekPoints={svg.weekPoints} />

      {recalibratedCount > 0 ? (
        <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Badge variant="amber">RECALIBRATED</Badge>
          <span
            style={{
              fontFamily: "var(--sa-font-mono)",
              fontSize: 11,
              color: "var(--sa-fg-muted)",
              letterSpacing: "0.08em",
            }}
          >
            {String(recalibratedCount)} cycle{recalibratedCount === 1 ? "" : "s"} recalibrated
          </span>
        </div>
      ) : null}

      {!pro && cycles.length > 4 ? (
        <div
          style={{
            marginTop: 12,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 10,
            color: "var(--sa-fg-muted)",
            letterSpacing: "0.12em",
          }}
        >
          🔒 Pro unlocks the full timeline + projections beyond 2 weeks ahead.
        </div>
      ) : null}

      {/* Hidden hint for N06 — recalibrated cycles still surface in the
          chart via the chip above. Keeps the panel honest about
          recalibration state without per-row clutter. */}
      <span style={{ display: "none" }} data-meta="recalibrated">
        {cycles.filter((c) => c.recalibrated).map((c) => `Week ${String(c.cycle_number)} recalibrated: ${c.recalibration_reason ?? ""}`).join(" | ")}
      </span>

      {/* Pill kept for E2E discoverability of pace state */}
      <span style={{ display: "none" }}>
        <Pill>{svg.onPace ? "ON PACE" : "OFF PACE"}</Pill>
      </span>
    </div>
  );
}

/**
 * W01..W13 stat cards row (frame_018).
 * Composes Eyebrow + Pill primitives only. No new tokens.
 * Each card: Wxx eyebrow + target×  + amount (formatCompactAmount).
 * Current week: ★ + brighter background.
 * Completed weeks: full opacity.
 * Projected weeks: muted opacity 0.65.
 * Recalibrated weeks: RECAL Pill red below amount.
 */
function WeekCardsRow({ weekPoints }: { weekPoints: PlanTimelineWeekPoint[] }): JSX.Element {
  return (
    <div
      data-testid="sika-week-cards-row"
      style={{
        marginTop: 16,
        display: "flex",
        gap: 6,
        overflowX: "auto",
        paddingBottom: 4,
      }}
    >
      {weekPoints.map((p) => {
        const isCurrent = p.status === "current";
        const isProjected = p.status === "projected";
        const cardBg = isCurrent
          ? "var(--sa-bg-elevated)"
          : "transparent";
        const cardBorder = isCurrent
          ? "1px solid var(--sa-accent-saffron)"
          : "1px solid var(--sa-hairline)";
        const opacity = isProjected ? 0.65 : 1;
        return (
          <div
            key={p.week}
            data-week={p.week}
            data-status={p.status}
            data-recalibrated={p.recalibrated ? "true" : "false"}
            style={{
              flex: "0 0 auto",
              minWidth: 64,
              padding: "8px 10px",
              border: cardBorder,
              borderRadius: 3,
              background: cardBg,
              opacity,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 4,
            }}
          >
            <div
              style={{
                fontFamily: "var(--sa-font-mono)",
                fontSize: 9,
                letterSpacing: "0.12em",
                color: "var(--sa-fg-muted)",
                textTransform: "uppercase",
              }}
            >
              {isCurrent ? "★ " : ""}W{String(p.week).padStart(2, "0")}
            </div>
            <div
              style={{
                fontFamily: "var(--sa-font-stat)",
                fontSize: 12,
                color: isProjected
                  ? "var(--sa-fg-muted)"
                  : "var(--sa-fg-secondary)",
                letterSpacing: "0.02em",
              }}
            >
              {p.target_mult.toFixed(2)}×
            </div>
            <div
              style={{
                fontFamily: "var(--sa-font-stat)",
                fontSize: 13,
                color: isCurrent
                  ? "var(--sa-accent-saffron)"
                  : "var(--sa-fg-primary)",
                fontWeight: isCurrent ? 600 : 400,
              }}
            >
              {formatCompactAmount(p.amount)}
            </div>
            {p.recalibrated ? (
              <div
                style={{
                  fontFamily: "var(--sa-font-mono)",
                  fontSize: 8,
                  letterSpacing: "0.16em",
                  color: "var(--sa-signal-red)",
                }}
              >
                RECAL
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// Re-export derivePaceState so tests + future consumers can read it
// from the panel module (matches the helper API).
export { derivePaceState };
